import { createContext, useContext, useMemo } from 'react';
import { DelegationSDK, ethereumAddressToAccountId } from '@chainflip/delegation';
import { isNotNullish } from '@chainflip/utils/guard';
import { useQuery } from '@tanstack/react-query';
import assert from 'assert';
import { erc20Abi } from 'viem';
import { useAccount, usePublicClient, useWalletClient } from 'wagmi';
import useAccountInfo from '@/shared/hooks/useAccountInfo';
import useIsEvmAccountInitialized from '@/shared/hooks/useIsEvmAccountInitialized';
import { chainflipAssetMap, getChainflipNetwork } from '@/shared/utils/env';
import TokenAmount from '@/shared/utils/TokenAmount';

type EpochDelegationInfo = {
  delegatedBalance: TokenAmount;
  undelegatedBalance: TokenAmount;
  operatorIdSs58: `cF${string}` | null;
};

type EvmScAccount = {
  connected: true;
  isLoading: boolean;
  idSs58: `cF${string}`;
  flipBalance: TokenAmount;
  evmFlipBalance: TokenAmount | null;
  currentEpoch: EpochDelegationInfo;
  nextEpoch: EpochDelegationInfo;
  redeemableBalance: TokenAmount;
  delegableBalance: TokenAmount;
  refresh: () => void;
  delegationSDK: {
    approve: (amount: TokenAmount) => Promise<`0x${string}` | null>;
    depositAndDelegate: (
      operator: `cF${string}`,
      depositAmount: TokenAmount,
      delegationAmount?: TokenAmount,
    ) => Promise<`0x${string}`>;
    delegateFromBalance: (
      operator: `cF${string}`,
      increaseBy?: TokenAmount,
    ) => Promise<`0x${string}`>;
    undelegate: (decreaseBy?: TokenAmount) => Promise<`0x${string}`>;
    redeem: (amount?: TokenAmount) => Promise<`0x${string}`>;
  };
};

type DisconnectedEvmScAccount = {
  connected: false;
  isLoading: boolean;
} & { [key in Exclude<keyof EvmScAccount, 'connected' | 'isLoading'>]?: undefined };

const Context = createContext<EvmScAccount | DisconnectedEvmScAccount | null>(null);

export const EvmScAccountProvider = ({ children }: { children: React.ReactNode }) => {
  const account = useAccount();
  const client = usePublicClient();

  const initialized = useIsEvmAccountInitialized();

  const { data: walletClient, isLoading: isLoadingWallet } = useWalletClient();
  const publicClient = usePublicClient();

  const idSs58 = useMemo(
    () => (walletClient ? ethereumAddressToAccountId(walletClient.account.address) : null),
    [walletClient],
  );

  const {
    data,
    refetch,
    isLoading: isLoadingAccountInfo,
  } = useAccountInfo(idSs58, {
    refetchInterval: 5_000,
  });

  const isLoading = isLoadingWallet || isLoadingAccountInfo || !initialized;

  const { data: evmFlipBalance = null } = useQuery({
    queryKey: ['flipBalance', Boolean(client), account.address],
    queryFn: async () => {
      if (!client || !account.address) return null;
      return client.readContract({
        address: chainflipAssetMap.Flip.address as `0x${string}`,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [account.address],
      });
    },
    enabled: isNotNullish(account.address) && isNotNullish(client),
  });

  const delegationSDK = useMemo(() => {
    const network = getChainflipNetwork();
    if (!walletClient || !publicClient || network === 'unknown') return null;

    // wagmi resolves viem 2.46.x while @chainflip/delegation pins viem 2.48.8+, so the
    // wallet/public clients are structurally identical but nominally distinct to TS. Bridge
    // to the SDK's own constructor types — they remain compatible at runtime.

    // TODO(2.2): bump viem and wagmi to avoid cast
    type DelegationSDKArgs = ConstructorParameters<typeof DelegationSDK>;
    const sdk = new DelegationSDK(
      walletClient as unknown as DelegationSDKArgs[0],
      publicClient as unknown as DelegationSDKArgs[1],
      network,
    );

    return {
      approve: (amount: TokenAmount) => sdk.approveScUtils(amount.toBigInt()),
      depositAndDelegate: (
        operator: `cF${string}`,
        depositAmount: TokenAmount,
        increaseBy?: TokenAmount,
      ) => sdk.depositAndDelegate(operator, depositAmount.toBigInt(), increaseBy?.toBigInt()),
      delegateFromBalance: (operator: `cF${string}`, increaseBy?: TokenAmount) =>
        sdk.delegateFromBalance(operator, increaseBy?.toBigInt()),
      undelegate: (decreaseBy?: TokenAmount) => sdk.undelegate(decreaseBy?.toBigInt()),
      redeem: (amount?: TokenAmount) =>
        sdk.redeem(walletClient.account.address, amount?.toBigInt()),
    };
  }, [walletClient, publicClient]);

  const evmScAccount = useMemo(() => {
    if (!data || !idSs58 || !delegationSDK) return { connected: false as const, isLoading };

    const flipBalance = TokenAmount.fromAsset(data.flip_balance, 'Flip');

    const currentDelegation = data.current_delegation_status;

    const currentEpoch = currentDelegation
      ? {
          delegatedBalance: TokenAmount.fromAsset(currentDelegation.bid, 'Flip'),
          undelegatedBalance: flipBalance.sub(currentDelegation.bid.toString()),
          operatorIdSs58: currentDelegation.operator,
        }
      : {
          delegatedBalance: TokenAmount.ZERO,
          undelegatedBalance: flipBalance,
          operatorIdSs58: null,
        };

    const upcomingDelegation = data.upcoming_delegation_status;
    const nextEpoch = upcomingDelegation
      ? {
          delegatedBalance: TokenAmount.fromAsset(upcomingDelegation.bid, 'Flip'),
          undelegatedBalance: flipBalance.sub(upcomingDelegation.bid.toString()),
          operatorIdSs58: upcomingDelegation.operator,
        }
      : {
          delegatedBalance: TokenAmount.ZERO,
          undelegatedBalance: flipBalance,
          operatorIdSs58: null,
        };

    return {
      flipBalance,
      redeemableBalance: TokenAmount.fromAsset(data.estimated_redeemable_balance, 'Flip'),
      delegableBalance: nextEpoch.undelegatedBalance.add(evmFlipBalance?.toString() ?? '0'),
      currentEpoch,
      nextEpoch,
      evmFlipBalance: TokenAmount.fromAsset(evmFlipBalance, 'Flip'),
      refresh: refetch,
      delegationSDK,
      idSs58,
      isLoading,
      connected: true as const,
    } satisfies EvmScAccount;
  }, [
    data?.flip_balance,
    data?.current_delegation_status?.bid,
    data?.current_delegation_status?.operator,
    data?.upcoming_delegation_status?.bid,
    data?.upcoming_delegation_status?.operator,
    data?.estimated_redeemable_balance,
    evmFlipBalance,
    delegationSDK,
    isLoading,
  ]);

  return <Context.Provider value={evmScAccount}>{children}</Context.Provider>;
};

export default function useEvmScAccount(): EvmScAccount | DisconnectedEvmScAccount;
export default function useEvmScAccount(assertConnected: true): EvmScAccount;
export default function useEvmScAccount(
  assertConnected?: true,
): EvmScAccount | DisconnectedEvmScAccount {
  const context = useContext(Context);

  assert(context, 'useEvmScAccount must be used within a EvmScAccountProvider');

  assert(!assertConnected || context.connected, 'Account is not connected');

  return context;
}
